import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth, roleHome } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "", role: "client" });
  const [busy, setBusy] = useState(false);
  const [inlineError, setInlineError] = useState(null);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const { login, register, user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  // iter93: /start page links here with ?tab=signup&launch=1 — honour both.
  const [tab, setTab] = useState(sp.get("tab") === "signup" ? "signup" : "signin");
  const launchPartner = sp.get("launch") === "1";

  // A `next` that is only a shell's front door ("/hub", "/admin") is not a deep link — it means
  // the session lapsed while they were standing at the door, so an admin still gets asked which
  // area they want. A real deep link (a case, a settings page) is honoured and skips the chooser.
  const SHELL_ROOTS = ["/hub", "/hub/", "/admin", "/admin/", "/admin/cleanup", "/choose", "/"];
  const landing = (u) => {
    const home = roleHome(u.role);
    if (next && !SHELL_ROOTS.includes(next)) return next;
    return home;
  };

  useEffect(() => {
    if (user) nav(landing(user), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, nav, next]);

  const onSignin = async (e) => {
    e.preventDefault(); setBusy(true); setInlineError(null);
    const res = await login(form.email, form.password, needs2FA ? totpCode : undefined);
    setBusy(false);
    if (res.needs_2fa) {
      setNeeds2FA(true);
      toast.info("Enter your 2FA code from your authenticator app.");
      return;
    }
    if (!res.ok) {
      setInlineError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Welcome back.");
    nav(landing(res.user), { replace: true });
  };

  const onRegister = async (e) => {
    e.preventDefault(); setBusy(true); setInlineError(null);
    const res = await register({ email: form.email, password: form.password, name: form.name, company: form.company, phone: form.phone || null, role: form.role });
    setBusy(false);
    if (!res.ok) {
      setInlineError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Account created.");
    nav(roleHome(res.user.role), { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.45) 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[420px] rounded-full bg-primary/30 blur-3xl" />
        <Link to="/" className="relative inline-flex items-center gap-2.5">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-white text-slate-900">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 7h7M4 12h12M4 17h7" strokeLinecap="round" />
              <circle cx="18" cy="7" r="2.2" fill="#0055FF" stroke="none" />
              <circle cx="18" cy="17" r="2.2" fill="#0055FF" stroke="none" />
            </svg>
          </span>
          <span className="text-[15px] font-semibold tracking-tight">Connexis<span className="text-primary">.</span></span>
        </Link>
        <div className="relative max-w-md">
          <ShieldCheck className="size-5 text-primary" />
          <p className="mt-4 text-2xl font-medium tracking-tight leading-snug">&ldquo;The only partner where routing, AI qualification and the buyer portal feel like one product.&rdquo;</p>
          <p className="mt-4 text-sm text-slate-400">Sarah Chen — Northbridge Mortgage Co.</p>
        </div>
        <div className="relative text-xs text-slate-500">© {new Date().getFullYear()} Connexis Group Ltd.</div>
      </div>

      <div className="flex flex-col justify-center px-6 py-12 sm:px-12">
        <div className="mx-auto w-full max-w-md">
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-900">← Back to site</Link>
          <h1 className="mt-6 text-3xl tracking-tight font-semibold text-slate-900">Welcome to Connexis.</h1>
          <p className="mt-2 text-sm text-slate-600" data-testid="auth-subtitle">
            {tab === "signin"
              ? "Sign in to your client, admin, affiliate or publisher portal."
              : "Open a client or affiliate account. Publishers are vetted separately."}
          </p>

          <Tabs value={tab} onValueChange={setTab} className="mt-8">
            <TabsList className="grid grid-cols-2 w-full" data-testid="auth-tabs">
              <TabsTrigger value="signin" data-testid="tab-signin">Sign in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup">Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="signin" className="mt-6">
              <form onSubmit={onSignin} className="space-y-4" data-testid="signin-form">
                {inlineError && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-800 flex items-start gap-2" data-testid="signin-error">
                    <ShieldCheck className="size-4 mt-0.5 shrink-0 text-rose-600" />
                    <span>{inlineError}</span>
                  </div>
                )}
                <div><Label>Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signin-email" disabled={needs2FA} /></div>
                <div><Label>Password</Label><Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="signin-password" disabled={needs2FA} /></div>
                {needs2FA && (
                  <div className="rounded-xl border border-[#0055FF]/30 bg-[#0055FF]/[0.04] p-4" data-testid="signin-2fa-block">
                    <Label className="flex items-center gap-2 text-[13px] font-medium text-slate-900">
                      <ShieldCheck className="size-4 text-[#0055FF]" /> 2-factor code
                    </Label>
                    <p className="text-[12px] text-slate-600 mt-1">Open your authenticator app and enter the 6-digit code. You can also enter a backup code.</p>
                    <Input
                      required
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9a-fA-F-]/g, "").slice(0, 14))}
                      placeholder="123456"
                      autoFocus
                      className="mt-3 font-mono text-lg tracking-[0.2em]"
                      data-testid="signin-totp"
                    />
                  </div>
                )}
                <Button disabled={busy} type="submit" className="w-full rounded-full h-11" data-testid="signin-submit">{busy ? "Signing in…" : <>{needs2FA ? "Verify code" : "Sign in"} <ArrowRight className="size-4" /></>}</Button>
                {needs2FA && (
                  <button type="button" onClick={() => { setNeeds2FA(false); setTotpCode(""); setInlineError(null); }} className="text-[12px] text-slate-500 hover:text-[#0055FF] w-full text-center" data-testid="signin-2fa-cancel">
                    ← Sign in with a different account
                  </button>
                )}
                {!needs2FA && (
                  <div className="text-center">
                    <Link to="/forgot-password" className="text-[12px] text-slate-500 hover:text-[#0055FF]" data-testid="signin-forgot-password">
                      Forgot your password?
                    </Link>
                  </div>
                )}
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-6">
              {launchPartner && form.role === "client" && (
                <div className="mb-5 rounded-md border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4" data-testid="launch-partner-signup-banner">
                  <div className="flex items-start gap-3">
                    <span className="inline-flex size-9 items-center justify-center rounded-sm bg-emerald-600 text-white shrink-0">
                      <Sparkles className="size-4" strokeWidth={1.6} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-emerald-900 leading-tight">
                        You're claiming a Launch Partner seat
                      </div>
                      <div className="text-[12px] text-emerald-800/85 mt-1 leading-snug">
                        +20% wallet bonus on your first £5,000+ top-up · 30 days of dedicated onboarding · priority publisher routing. Auto-applied once you complete signup.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <form onSubmit={onRegister} className="space-y-4" data-testid="signup-form">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Full name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name" /></div>
                  <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="signup-company" /></div>
                </div>
                <div><Label>Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email" /></div>
                <div>
                  <Label>Mobile <span className="text-slate-400 font-normal">(optional, for SMS updates)</span></Label>
                  <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+44 7700 900000" data-testid="signup-phone" />
                </div>
                <div><Label>Password</Label><Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="signup-password" /></div>
                <div>
                  <Label>Account type</Label>
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    {[["client", "Client / Buyer"], ["affiliate", "Affiliate"]].map(([v, l]) => (
                      <button type="button" key={v} onClick={() => setForm({ ...form, role: v })} className={`rounded-md border px-3 py-2 text-sm ${form.role === v ? "border-primary bg-primary/[0.04] text-slate-900" : "border-slate-200 text-slate-600"}`} data-testid={`role-${v}`}>{l}</button>
                    ))}
                  </div>
                  <p className="mt-3 text-[12px] text-slate-500">
                    Looking for publisher access?{" "}
                    <Link to="/publisher/apply" className="text-[#0055FF] hover:underline font-medium" data-testid="signup-publisher-link">
                      Apply here &rarr;
                    </Link>
                    <span className="block mt-0.5 text-slate-400">Publishers are vetted by application — admin approves before access is granted.</span>
                  </p>
                </div>
                <Button disabled={busy} type="submit" className="w-full rounded-full h-11" data-testid="signup-submit">{busy ? "Creating…" : <>Create account <ArrowRight className="size-4" /></>}</Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
